import {
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { FormikHelpers } from 'formik';
import { filter, isEmpty, omitBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikPanel, SidebarLayout, SidebarSize } from 'components';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition } from 'types';
import { deviceUtils } from 'utils';

import { FiltersDropdownForm } from './FiltersDropdownForm';

type Props = {
  definitions: FilterDefinition[];
  initialValues?: any;
  filters?: any;
  onReset: () => void;
  onSubmit: (values: any) => void;
};

export type FiltersDropdownProps = Props;

export const FiltersDropdown: React.FC<Props> = ({
  definitions,
  filters,
  initialValues: initialValuesProp,
  onReset,
  onSubmit,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.filters_dropdown',
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMobile = useMemo(() => deviceUtils.isMobile(), []);

  const visibleDefinitions = useMemo(
    () => filter(definitions, (definition) => !definition.hidden),
    [definitions],
  );

  const open = useMemo(() => !!anchorEl, [anchorEl]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleReset = useCallback(() => {
    onReset();
    handleClose();
  }, [handleClose, onReset]);

  const handleSubmit = useCallback(
    (values: any, helpers: FormikHelpers<any>) => {
      helpers.setSubmitting(false);
      onSubmit(values);
      handleClose();
    },
    [handleClose, onSubmit],
  );

  const filtersDirty = useMemo(
    () => !isEmpty(omitBy(filters, isEmpty)),
    [filters],
  );

  return (
    <div>
      <IconButton data-testid="filters-open" onClick={handleClick}>
        <FilterListIcon color={filtersDirty ? 'info' : 'inherit'} />
      </IconButton>
      {isMobile ? (
        <SidebarLayout
          sidebarProps={{
            size: SidebarSize.md,
            open,
            onClose: handleClose,
            title: t('title'),
            drawerProps: {
              hideBackdrop: false,
              disableScrollLock: false,
            },
            content: (
              <FiltersDropdownForm
                initialValues={initialValuesProp}
                definitions={visibleDefinitions}
                onReset={handleReset}
                onSubmit={handleSubmit}
              />
            ),
          }}
        />
      ) : (
        <FormikPanel
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          placement="bottom"
          paperClassName="tw-border tw-px-4 tw-py-2 !tw-w-72 tw-overflow-y-auto tw-max-h-[calc(100vh-12px)]"
        >
          <div className="tw-flex tw-items-center">
            <div className="tw-text-lg tw-font-bold tw-flex-1">
              {t('title')}
            </div>
            <CloseIcon className="tw-cursor-pointer" onClick={handleClose} />
          </div>
          <FiltersDropdownForm
            initialValues={initialValuesProp}
            definitions={visibleDefinitions}
            onReset={handleReset}
            onSubmit={handleSubmit}
          />
        </FormikPanel>
      )}
    </div>
  );
};
